import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Map, Calculator, FileText, Wrench, FolderOpen, 
  Home, Info, DollarSign, BookOpen
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HTMLSitemapPage = () => {
  const [tools, setTools] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toolsRes, postsRes] = await Promise.all([
          fetch(`${API_URL}/api/tools`),
          fetch(`${API_URL}/api/blog`)
        ]);
        const toolsData = await toolsRes.json();
        const postsData = await postsRes.json();
        setTools(toolsData);
        setPosts(postsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Group tools by category
  const toolsByCategory = tools.reduce((acc, tool) => {
    const category = tool.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tool);
    return acc;
  }, {});

  // Group posts by tags
  const cryptoPosts = posts.filter(p => 
    p.tags?.some(t => ['Crypto', 'Bitcoin', 'Ethereum', 'Calculator'].includes(t))
  );
  const devPosts = posts.filter(p => 
    p.tags?.some(t => ['Developer', 'Programming', 'Tools'].includes(t))
  );

  const mainPages = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'All Tools', path: '/all-tools', icon: Wrench },
    { name: 'Crypto Calculators', path: '/crypto-calculators', icon: Calculator },
    { name: 'Top Tools', path: '/top-tools', icon: Calculator },
    { name: 'Latest Updates', path: '/latest-updates', icon: FileText },
    { name: 'Blog', path: '/blog', icon: BookOpen },
    { name: 'Pricing', path: '/pricing', icon: DollarSign },
    { name: 'About', path: '/about', icon: Info },
  ];

  const categories = [
    { name: 'Text Tools', path: '/category/text-tools' },
    { name: 'Developer Tools', path: '/category/developer-tools' },
    { name: 'SEO Tools', path: '/category/seo-tools' },
    { name: 'Generators', path: '/category/generators' },
    { name: 'Converters', path: '/category/converters' },
    { name: 'Calculator Tools', path: '/category/calculator-tools' },
  ];

  return (
    <>
      <SEOHead
        title="Sitemap - All Pages & Tools | ToolsForge"
        description="Complete sitemap of ToolsForge. Browse all 182+ tools, 49+ blog articles, and category pages. Find crypto calculators, developer tools, and more."
        keywords="toolsforge sitemap, all tools, crypto calculators list, developer tools list"
        canonical="https://toolsforgeapp.com/sitemap"
      />

      <div className="min-h-screen py-20 px-6" data-testid="html-sitemap-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <span className="text-foreground">Sitemap</span>
          </nav>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-primary/30 text-primary">
              <Map className="w-3 h-3 mr-2" />
              Complete Index
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Site Map
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Browse all pages, tools, and content on ToolsForge. 
              {!loading && ` ${tools.length} tools, ${posts.length} articles.`}
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading sitemap...</div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Main Pages */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-primary" />
                  Main Pages
                </h2>
                <ul className="space-y-2">
                  {mainPages.map(page => (
                    <li key={page.path}>
                      <Link 
                        to={page.path} 
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <page.icon className="w-4 h-4" />
                        {page.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Categories */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-primary" />
                  Categories
                </h2>
                <ul className="space-y-2">
                  {categories.map(cat => (
                    <li key={cat.path}>
                      <Link 
                        to={cat.path} 
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Crypto Calculators */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-orange-400" />
                  Crypto Calculators ({toolsByCategory['calculator-tools']?.length || 0})
                </h2>
                <ul className="space-y-1 max-h-64 overflow-y-auto">
                  {(toolsByCategory['calculator-tools'] || []).map(tool => (
                    <li key={tool.slug}>
                      <Link 
                        to={`/tools/${tool.slug}`} 
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {tool.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Developer Tools */}
              <section className="p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-blue-400" />
                  Developer Tools ({toolsByCategory['developer-tools']?.length || 0})
                </h2>
                <ul className="space-y-1 max-h-64 overflow-y-auto">
                  {(toolsByCategory['developer-tools'] || []).map(tool => (
                    <li key={tool.slug}>
                      <Link 
                        to={`/tools/${tool.slug}`} 
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {tool.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              {/* All Tools by Category */}
              <section className="lg:col-span-2 p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary" />
                  All Tools by Category ({tools.length})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(toolsByCategory).map(([category, categoryTools]) => (
                    <div key={category}>
                      <h3 className="font-semibold text-sm mb-2 capitalize text-primary">
                        {category.replace(/-/g, ' ')} ({categoryTools.length})
                      </h3>
                      <ul className="space-y-1">
                        {categoryTools.slice(0, 10).map(tool => (
                          <li key={tool.slug}>
                            <Link 
                              to={`/tools/${tool.slug}`} 
                              className="text-xs text-muted-foreground hover:text-primary transition-colors"
                            >
                              {tool.name}
                            </Link>
                          </li>
                        ))}
                        {categoryTools.length > 10 && (
                          <li className="text-xs text-muted-foreground">
                            +{categoryTools.length - 10} more...
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {/* Blog Posts */}
              <section className="lg:col-span-2 p-6 rounded-xl border border-border bg-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-400" />
                  Blog Articles ({posts.length})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {posts.map(post => (
                    <Link 
                      key={post.slug}
                      to={`/blog/${post.slug}`} 
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {post.title}
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          )}

          {/* XML Sitemap Link */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-2">
              For search engines:
            </p>
            <a 
              href="/api/sitemap.xml" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              View XML Sitemap →
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default HTMLSitemapPage;
